import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import { Message as MessageInterface } from 'src/app/models/Message';
import {MessageAttachment} from '../../../models/MessageAttachment';
import {MessageButtonType} from '../../../enums/message-button-type';
import {EnvironmentService} from "../../../services/environment.service";
import { Store, select } from '@ngrx/store';
import { getTemplates } from '../../../templates/state/selectors';
import { Template } from '../../../models/Template';
import { Observable, takeWhile } from 'rxjs';
import { isEmbedMode } from '../../../state/selectors';
import { Client } from "../../../models/Client";
import { SaveSingleMessageToPms } from '../../../messages/state/actions';
import { isSaveSingleMessageToPmsLoading } from '../../../messages/state/selectors';
import { AppState } from '../../../state/reducers';
import { Patient } from '../../../models/Patient';
import { getPatients } from '../../../patients/state/selectors';
import { IncrementPatientPage } from '../../../patients/state/actions';
import { getClientLoading } from '../../../conversation/state/selectors';
import { TranslateService } from '@ngx-translate/core';
import { Channel } from '../../../enums/channel';

@Component({
  selector: 'message',
  templateUrl: './message.component.html',
  styleUrls: ['./message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Message implements OnInit, OnDestroy {
  @Input() message?: MessageInterface;
  @Input() channel?: Channel;
  @Input() showDate?: boolean;
  @Input() client: Client | null = null;
  @Input() touchScreen = false;
  @Input() hideTimeStamp = false;
  @Input() fullWidth = false;
  @Output() loadedAttachment = new EventEmitter();
  @Output() delete = new EventEmitter<string>();
  alive: boolean = true;
  buttonType = MessageButtonType;
  showErrorInfoLink = false;
  helpLink = '';
  template: Template | null = null;
  embedMode$: Observable<boolean>;
  saveToPmsLoading$: Observable<boolean>;
  patientsLoading = false;
  patientsPage = 0;
  patients: Patient[] = [];
  selectedPatient: Patient | undefined;
  showPatientSelector: boolean = false;
  isSmsChannel: boolean = false;

  constructor(private environmentService: EnvironmentService, private store: Store<AppState>, private translateService: TranslateService) {
    this.embedMode$ = this.store.pipe(select(isEmbedMode)).pipe(takeWhile(() => this.alive));
    this.saveToPmsLoading$ = this.store.pipe(select(isSaveSingleMessageToPmsLoading)).pipe(takeWhile(() => this.alive));
  }

  ngOnInit(): void {
    if (
      this.message &&
      this.message.error &&
      this.message.content.includes('Error: the client has sent an unsupported message type')
    ) {
      this.showErrorInfoLink = true;
    }

    this.store.pipe(select(getTemplates)).pipe(takeWhile(() => this.alive)).subscribe((templates) => {
      return templates.filter((template) => {
        if (parseInt(template.id) == this.message?.templateId) {
          this.template = template;
        }
      });
    });

    this.helpLink = "https://help.digitalpractice.vet/en/collections/12609604-faq";
    this.subscribeToClientPatientLoading();
    this.subscribeToNewConversationPatients();

    this.isSmsChannel = this.channel === Channel.SMS || this.channel === Channel.SMSInfobip;
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  getHelpLink(): void {
  }

  loadedAttached(): void {
    this.loadedAttachment.emit();
  }

  trackAttachment(index: number, attachment: MessageAttachment): number | undefined {
    return attachment ? attachment.id : undefined;
  }

  deleteMessage(id: string): void {
    if (confirm(this.translateService.instant('ui.message.confirm'))) {
      this.delete.emit(id);
    }
  }

  saveMessageToClientRecord(id: string): void {
    this.showPatientSelector = true;
  }

  subscribeToClientPatientLoading(): void {
    this.store
      .pipe(select(getClientLoading))
      .pipe(takeWhile(() => this.alive))
      .subscribe((patientLoading) => {
        this.patientsLoading = patientLoading
      });
  }

  subscribeToNewConversationPatients(): void {
    this.store
      .pipe(select(getPatients))
      .pipe(takeWhile(() => this.alive))
      .subscribe((patients) => {
        this.patients = patients;
      });
  }

  selectPatient(patient: Patient){
    this.selectedPatient = patient;
    if (this.client && this.message && this.selectedPatient) { 
      this.store.dispatch(SaveSingleMessageToPms({
        message: this.message, 
        client: this.client,
        patient: this.selectedPatient
      }));
      this.showPatientSelector = false;
    }
  }

  handleMorePatients(): void {
    this.store.dispatch(IncrementPatientPage());
  }

  handleSendToClient(data: boolean): void {
    if (this.client && this.message) {
      this.store.dispatch(SaveSingleMessageToPms({
        message: this.message, 
        client: this.client
      }));
      this.showPatientSelector = false;
    }
  }
}
